<!--
@Author: Kleber Pinel <kleberdasilva>
@Date:   2019-03-12T16:00:13-07:00
@Email:  kleber@hackcapital.com
@Last modified by:   kleberdasilva
@Last modified time: 2019-03-13T12:24:05-07:00
-->

<template lang="pug">
  section
    .message__body.py-2.px-3(
      :class="{ 'green lighten-5': !isIncoming(message), 'blue-grey lighten-5': isIncoming(message) }"
    )
      v-layout(row wrap)
        v-flex.xs12.break-word
          div(v-if="!messageValues" v-html="safeHTML(message.body)")
          div(v-else) {{ safePlainText(messageValues.text) }}
        v-flex.xs12.video-wrapper(v-for="(video, index) in message.videos" :key="index")
          .video-thumb(v-if="video.thumbnail_url" @click="videoClicked(video)")
            img(:src="video.thumbnail_url" :alt="video.title" :title="video.title")
            v-icon play_arrow
          .video-thumb.missing-video(v-else :title="video.title")
            v-icon broken_image
        v-flex(v-if="messageValues")
          div(v-for="(image, index) in messageValues.images" :key="index")
            img(:src="image")
        v-flex.xs12.message__error(v-if="isSMSNotDelivered()")
          .message__error__description
            | Error 30007 Text undelivered - The receiving carrier rejected the message, try changing the wording of the text or removing any hyper links.
          a.message__error_link(
            href="https://lonewolf.my.site.com/s/article/LD-Failed-texts-due-to-message-filtering-and-carrier-filtering-LionDesk"
            target="_blank"
          ) 
            | Learn more about this error.
        v-flex.xs12.message__error(v-if="isSMSMobilePhoneInvalid(message.error_code)")
          .message__error__description
            | Unable to send SMS. Recipient number is not a valid mobile number.
        v-flex.xs12.message__error(v-else-if="isSMS10RegInvalid(message.error_code)")
          v-icon.message__error__title.pr-1 error_outline
          span.message__error__title SMS Not Delivered - No Registration Found -&nbsp;
          span(v-html="safeHTML(message.error.message)")
        v-flex.xs12.message__error(v-else-if="isSMSNotSent(message.error_code)")
          v-icon.message__error__title.pr-1 error_outline
          span.message__error__title SMS Not Sent -&nbsp;
          span(v-html="safeHTML(message.error.message)")
        v-flex.xs12.message__error(v-else-if="isSMSWithErrorMessage()")
          v-icon.message__error__title warningzo
          span.message__error__title Text Failed To Send -&nbsp;
          span {{ message.error.message }}

    footer.pt-1.px-1(:class="{ 'text-xs-right': !isIncoming(message) }")
      span.message__sender(v-if="isIncoming(message)")
        span.px-1.font-weight-bold {{ contactDetails | getFullName | replaceBlankName }}

      span.message__sender(v-else)
        span.px-1.font-weight-bold You
      span.time-sent {{ message.created_at | distanceInWordsToNow }} ago
</template>

<script>
import { DIRECTIONS } from '@/Communications/config';
import { safeHTML, safePlainText } from '@/Core/filters/safeContent';
import replaceBlankName from '@/Core/filters/replaceBlankName';
import getFullName from '@/Core/filters/getFullName';
import { getObjectfromHTMLComment } from '@/Core/utilities/formatJsonFromHtmlComment';
import { distanceInWordsToNow } from 'date-fns';

export default {
  data() {
    return {
      images: [],
      TEXT_MOBILE_PHONE_INVALID_ID: 40,
      TEXT_10DLC_REG_INVALID_ID: 41,
      TEXT_NOT_SENT_ID: 43,
    };
  },
  props: {
    message: Object,
    contactDetails: Object,
    onVideoClicked: Function,
  },
  filters: {
    getFullName,
    distanceInWordsToNow, // DateFns filter to calculate time distance
    replaceBlankName,
  },
  computed: {
    messageValues() {
      const values = getObjectfromHTMLComment(this.message.body);
      if (values) return values;
    },
    errorMessage() {
      if (this.messageValues) {
        return this.messageValues.error;
      }
      return this.message.error;
    },
  },
  methods: {
    safeHTML,
    safePlainText,
    // Determines if the current text is from the contact
    isIncoming: text => text.direction === DIRECTIONS[1].value,
    videoClicked(video) {
      this.onVideoClicked(video);
    },
    isSMSWithErrorMessage() {
      return (
        this.message.error && this.message.error.message && this.message.error.message.length > 0
      );
    },
    isSMSNotDelivered() {
      return this.message.error && this.isSMSNotDeliveredErrorCode(this.message.error.code);
    },
    isSMSNotDeliveredErrorCode(errorCode) {
      return errorCode === 30007;
    },
    isSMSMobilePhoneInvalid(errorCode) {
      return errorCode === this.TEXT_MOBILE_PHONE_INVALID_ID;
    },
    isSMS10RegInvalid(errorCode) {
      return errorCode === this.TEXT_10DLC_REG_INVALID_ID;
    },
    isSMSNotSent(errorCode) {
      return errorCode === this.TEXT_NOT_SENT_ID;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../Core/assets/scss/main.scss';
.message {
  .message__body {
    img {
      padding-top: 5px;
      width: 100%;
    }
    width: 80%;
    border-radius: var(--default-border-radius);
    .video-wrapper {
      padding-bottom: 0;
      &:first-child {
        padding-top: 16px;
      }
    }
    .video-thumb {
      width: 256px;
      position: relative;
      img {
        width: 100%;
        vertical-align: middle;
      }
      .v-icon {
        top: 50%;
        left: 50%;
        margin: -24px 0 0 -24px;
        position: absolute;
        font-size: 48px;
        background: white;
        cursor: pointer;
        border-radius: 50%;
      }
    }
    .missing-video {
      height: 144px;
      background: var(--v-background-lighten2);
      .v-icon {
        background: transparent;
      }
    }
  }

  &.outgoing {
    .message__body {
      margin-left: 20%;
      border-bottom-right-radius: 0;
    }
  }
  &.incoming {
    .message__body {
      border-bottom-left-radius: 0;
    }
  }

  .message__error,
  .activity_log_sms_error {
    background: white;
    border: 1px solid $lion-sms-error;
    border-radius: 5px;
    margin: 10px 8px 5px 0px;
    padding: 10px;
    .message__error__title {
      color: $lion-sms-error;
    }
  }

  .message__error__description {
    display: flex;
    &:before {
      font-family: 'Material Icons';
      font-weight: normal;
      content: 'error_outline';
      color: $lion-sms-error;
      width: 20px;
      height: 20px;
      float: left;
      margin: -4px 6px 0 0;
      font-size: 22px;
    }
  }
  .message__error_link {
    padding-left: 26px;
  }
}

// Reset inherited styles
.container.grid-list-lg .layout .flex,
.container.grid-list-xl .layout .flex {
  padding: 0px;
}
.container.grid-list-lg *:not(:only-child) .layout:last-child,
.container.grid-list-xl *:not(:only-child) .layout:last-child {
  margin-bottom: 0px;
}
.container.grid-list-lg *:not(:only-child) .layout:first-child,
.container.grid-list-xl *:not(:only-child) .layout:first-child {
  margin-top: 0px;
}
.container.grid-list-lg .layout:only-child,
.container.grid-list-xl .layout:only-child {
  margin: 0;
}
.container.grid-list-lg .layout .flex.message__error,
.container.grid-list-xl .layout .flex.message__error {
  padding: 10px;
}
</style>
